import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProducts, getEntry, saveEntry, reorderEntryProducts, reorderEntryCategoriesApi, getCategories, getPackagings } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

// Greek-friendly normalize: lowercase + remove accents
const normalizeText = (str) => (str || '').toString().toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function DailyEntry() {
  const { selectedFactory } = useAuth();
  const [products, setProducts] = useState([]);
  const [packagings, setPackagings] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingEntry, setExistingEntry] = useState(null);

  // Search state
  const [search, setSearch] = useState('');
  const [currentMatch, setCurrentMatch] = useState(-1);
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});

  // ==================== DRAG STATE (live-swap pattern, same as Products) ====================
  const dragType = useRef(null); // 'product' | 'cat'

  // product drag
  const dragItem = useRef(null);
  const dragCatName = useRef(null);
  const dragBackup = useRef(null);
  const lastTarget = useRef(null);
  const dropDone = useRef(false);
  const [draggingProductId, setDraggingProductId] = useState(null);

  // category drag - drop indicator approach (no live swap for categories)
  const catDragItem = useRef(null);
  const catDropDone = useRef(false);
  const [draggingCat, setDraggingCat] = useState(null);
  const [catDropIndicator, setCatDropIndicator] = useState(null); // {cat, position}

  const loadProducts = useCallback(() => {
    if (!selectedFactory) return;
    Promise.all([
      getProducts(selectedFactory.id, false, 'entry'),
      getPackagings()
    ])
      .then(([prodRes, pkgRes]) => {
        setProducts(prodRes.data);
        setPackagings(pkgRes.data);
      })
      .catch(console.error);
  }, [selectedFactory]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const loadEntry = useCallback(async () => {
    if (!selectedFactory || !date) return;
    setLoading(true);
    try {
      const res = await getEntry(selectedFactory.id, date);
      setExistingEntry(res.data);
      const q = {};
      res.data.items?.forEach(item => { q[item.product_id] = item.quantity; });
      setQuantities(q);
      setNotes(res.data.notes || '');
    } catch (err) {
      if (err.response?.status === 404) {
        setExistingEntry(null);
        setQuantities({});
        setNotes('');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedFactory, date]);

  useEffect(() => { loadEntry(); }, [loadEntry]);

  const handleQty = (productId, val) => {
    const n = parseInt(val);
    setQuantities(q => ({ ...q, [productId]: isNaN(n) || n < 0 ? 0 : n }));
  };

  const grandTotal = products.reduce(
    (sum, p) => sum + (parseFloat(p.price) * (quantities[p.id] || 0)),
    0
  );

  const handleSave = async () => {
    const items = products
      .filter(p => (quantities[p.id] || 0) > 0)
      .map(p => ({ product_id: p.id, quantity: quantities[p.id] }));

    if (items.length === 0) {
      toast.error('Εισάγετε ποσότητα για τουλάχιστον ένα προϊόν');
      return;
    }

    setSaving(true);
    try {
      await saveEntry(selectedFactory.id, { entry_date: date, notes, items });
      toast.success('Η καταχώρηση αποθηκεύτηκε!');
      loadEntry();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα αποθήκευσης');
    } finally {
      setSaving(false);
    }
  };

  // Categories in current order (derived from products' order from backend)
  const categories = useMemo(() => {
    const seen = new Set();
    const list = [];
    products.forEach(p => {
      const cat = p.category || 'Άλλα';
      if (!seen.has(cat)) { seen.add(cat); list.push(cat); }
    });
    return list;
  }, [products]);

  // Packaging label helper - shows (κενό) if packaging no longer exists
  const packagingLabel = (val) => {
    if (!val) return '(κενό)';
    const match = packagings.find(p => p.name.toLowerCase() === String(val).toLowerCase());
    return match ? match.name : '(κενό)';
  };

  // ============================================================
  // SEARCH
  // ============================================================
  const matches = useMemo(() => {
    const q = normalizeText(search.trim());
    if (!q) return [];
    const orderedIds = [];
    categories.forEach(cat => {
      products.filter(p => (p.category || 'Άλλα') === cat).forEach(p => {
        if (normalizeText(p.name).includes(q) || normalizeText(cat).includes(q)) {
          orderedIds.push(p.id);
        }
      });
    });
    return orderedIds;
  }, [search, products, categories]);

  useEffect(() => { setCurrentMatch(-1); }, [search]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    if (matches.length === 0) return;
    const nextIdx = (currentMatch + 1) % matches.length;
    const id = matches[nextIdx];
    const el = rowRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 1800);
    }
    setCurrentMatch(nextIdx);
  };

  // ============================================================
  // PRODUCT DRAG & DROP (live-swap — same as Products page)
  // ============================================================
  const onProductDragStart = (e, product, catName) => {
    dragType.current = 'product';
    dragItem.current = product;
    dragCatName.current = catName;
    dragBackup.current = null;
    lastTarget.current = null;
    dropDone.current = false;
    setDraggingProductId(product.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onProductDragOver = (e, targetProduct, catName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragType.current !== 'product') return;
    if (!dragItem.current || dragItem.current.id === targetProduct.id) return;
    if (dragCatName.current !== catName) return; // only within same category
    if (lastTarget.current === targetProduct.id) return;
    setProducts(prev => {
      if (!dragBackup.current) dragBackup.current = [...prev];
      const catProds = prev.filter(p => (p.category || 'Άλλα') === catName);
      const otherProds = prev.filter(p => (p.category || 'Άλλα') !== catName);
      const fi = catProds.findIndex(p => p.id === dragItem.current.id);
      const ti = catProds.findIndex(p => p.id === targetProduct.id);
      if (fi === -1 || ti === -1 || fi === ti) return prev;
      const n = [...catProds];
      n.splice(fi, 1);
      n.splice(ti, 0, dragItem.current);
      lastTarget.current = targetProduct.id;
      // rebuild full list preserving category order from `categories` state
      const result = [];
      categories.forEach(cn => {
        if (cn === catName) {
          n.forEach(p => result.push(p));
        } else {
          otherProds.filter(p => (p.category || 'Άλλα') === cn).forEach(p => result.push(p));
        }
      });
      return result;
    });
  };
  const onProductDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dropDone.current = true;
  };
  const onProductDragEnd = async () => {
    const wasDrop = dropDone.current;
    const backup = dragBackup.current;
    setDraggingProductId(null);
    dragType.current = null;
    dragItem.current = null;
    dragCatName.current = null;
    lastTarget.current = null;
    dropDone.current = false;
    if (!wasDrop) {
      // revert to backup if drag was cancelled
      if (backup) setProducts(backup);
      dragBackup.current = null;
      return;
    }
    dragBackup.current = null;
    // Send entire new order to backend (we reorder all products)
    setProducts(prev => {
      reorderEntryProducts(selectedFactory.id, prev.map(p => p.id)).catch(() => {
        toast.error('Σφάλμα αποθήκευσης σειράς');
        if (backup) setProducts(backup);
      });
      return prev;
    });
  };

  // ============================================================
  // CATEGORY DRAG & DROP (drop indicator — same as Products)
  // ============================================================
  const onCatDragStart = (e, cat) => {
    dragType.current = 'cat';
    catDragItem.current = cat;
    catDropDone.current = false;
    setDraggingCat(cat);
    setCatDropIndicator(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };
  const onCatDragOver = (e, targetCat) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragType.current !== 'cat') return;
    if (!catDragItem.current || catDragItem.current === targetCat) {
      setCatDropIndicator(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    setCatDropIndicator({ cat: targetCat, position });
  };
  const onCatDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setCatDropIndicator(null);
  };
  const onCatDrop = async (e, targetCat) => {
    if (dragType.current !== 'cat') return;
    e.preventDefault();
    e.stopPropagation();
    catDropDone.current = true;
    if (!catDragItem.current || catDragItem.current === targetCat) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const position = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    const draggedCat = catDragItem.current;
    const currentOrder = [...categories];
    const fromIdx = currentOrder.indexOf(draggedCat);
    if (fromIdx === -1) return;
    currentOrder.splice(fromIdx, 1);
    let toIdx = currentOrder.indexOf(targetCat);
    if (toIdx === -1) return;
    if (position === 'below') toIdx += 1;
    currentOrder.splice(toIdx, 0, draggedCat);

    // Rebuild products array in new category order
    const newProducts = [];
    currentOrder.forEach(cat => {
      products.filter(p => (p.category || 'Άλλα') === cat).forEach(p => newProducts.push(p));
    });
    setProducts(newProducts);

    // Save category order to backend
    try {
      const catRes = await getCategories(selectedFactory.id);
      const idByName = {};
      catRes.data.forEach(c => { idByName[c.name] = c.id; });
      const catIds = currentOrder.map(name => idByName[name]).filter(Boolean);
      if (catIds.length > 0) {
        await reorderEntryCategoriesApi(selectedFactory.id, catIds);
      }
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης σειράς κατηγοριών');
      loadProducts();
    }
  };
  const onCatDragEnd = () => {
    setDraggingCat(null);
    setCatDropIndicator(null);
    catDragItem.current = null;
    catDropDone.current = false;
    dragType.current = null;
  };

  const dragHandleStyle = {
    cursor: 'grab', padding: '0 6px', color: 'var(--gray-300)',
    fontSize: 16, userSelect: 'none', display: 'inline-flex', alignItems: 'center',
    flexShrink: 0,
  };
  const dropLineStyle = {
    height: 3, background: '#3b82f6', borderRadius: 2, margin: '4px 0',
  };

  return (
    <>
      <div className="page-header">
        <h2>Καταχώρηση Ημέρας</h2>
        <p>Καταχωρήστε τις ποσότητες παραγωγής για την επιλεγμένη ημερομηνία</p>
      </div>
      <div className="page-body">
        <div className="grid-2" style={{ alignItems: 'start' }}>
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-header">
                <span className="card-title">Ημερομηνία & Σημειώσεις</span>
                {existingEntry && <span className="badge badge-green">✓ Υπάρχει καταχώρηση</span>}
              </div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Ημερομηνία</label>
                  <input
                    type="date"
                    className="form-input"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Σημειώσεις (προαιρετικό)</label>
                  <textarea
                    className="form-textarea"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Π.χ. λειτούργησε μόνο πρωινή βάρδια..."
                    style={{ minHeight: 70 }}
                  />
                </div>
              </div>
            </div>

            <div className="total-bar">
              <span className="total-bar-label">Σύνολο Ημέρας</span>
              <span className="total-bar-amount">€{grandTotal.toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>

            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <><span className="loading-spinner" /> Αποθήκευση...</> : '💾 Αποθήκευση Καταχώρησης'}
            </button>
          </div>

          <div className="card" style={{ display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 140px)' }}>
            <div className="card-header" style={{ flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
              <span className="card-title">Προϊόντα</span>
              <span style={{ fontSize: 13, color: 'var(--gray-400)', marginLeft: 'auto' }}>{selectedFactory?.name}</span>
              {products.length > 0 && (
                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 6, alignItems: 'center', width: '100%', marginTop: 4 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type="text"
                      className="form-input"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="🔍 Αναζήτηση προϊόντος ή κατηγορίας..."
                      style={{ paddingRight: search ? 70 : 12, fontSize: 13, height: 34 }}
                    />
                    {search && (
                      <>
                        <span style={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, pointerEvents: 'none' }}>
                          {matches.length > 0 ? `${currentMatch < 0 ? 0 : currentMatch + 1}/${matches.length}` : '0'}
                        </span>
                        <button type="button" onClick={() => setSearch('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 16, lineHeight: 1, padding: 2 }} title="Καθαρισμός">✕</button>
                      </>
                    )}
                  </div>
                  <button type="submit" className="btn btn-secondary" disabled={!search || matches.length === 0} style={{ height: 34, padding: '0 14px', fontSize: 13 }}>
                    Επόμενο
                  </button>
                </form>
              )}
            </div>
            <div className="card-body" style={{ padding: '8px 20px', overflowY: 'auto', flex: 1 }}>
              {loading ? (
                <div className="page-loading"><span className="loading-spinner dark" /></div>
              ) : products.length === 0 ? (
                <div className="empty-state">
                  <div>📦</div>
                  <h3>Δεν υπάρχουν προϊόντα</h3>
                  <p>Προσθέστε προϊόντα από τη σελίδα Προϊόντα</p>
                </div>
              ) : (
                categories.map((cat, catIdx) => {
                  const showAbove = catDropIndicator?.cat === cat && catDropIndicator.position === 'above';
                  const showBelow = catDropIndicator?.cat === cat && catDropIndicator.position === 'below';
                  const isCatDragging = draggingCat === cat;
                  return (
                    <div key={cat}>
                      {showAbove && <div style={dropLineStyle} />}
                      <div
                        onDragOver={(e) => { if (dragType.current === 'cat') onCatDragOver(e, cat); }}
                        onDragLeave={(e) => { if (dragType.current === 'cat') onCatDragLeave(e); }}
                        onDrop={(e) => { if (dragType.current === 'cat') onCatDrop(e, cat); }}
                        style={{
                          opacity: isCatDragging ? 0.4 : 1,
                          transition: 'opacity 0.15s ease',
                          borderTop: catIdx !== 0 ? '1px solid var(--gray-100)' : 'none',
                          marginTop: catIdx !== 0 ? 8 : 0,
                        }}
                      >
                        {/* Category header - bold black */}
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          padding: '14px 0 8px',
                          fontSize: 14, fontWeight: 700, color: 'var(--gray-900)',
                          fontFamily: 'var(--font-display)',
                          letterSpacing: '-0.2px',
                        }}>
                          <span
                            draggable
                            onDragStart={(e) => onCatDragStart(e, cat)}
                            onDragEnd={onCatDragEnd}
                            style={{ ...dragHandleStyle, color: isCatDragging ? '#22c55e' : 'var(--gray-300)' }}
                            title="Σύρετε για αλλαγή σειράς κατηγορίας"
                          >⠿</span>
                          <span>{cat}</span>
                        </div>
                        {/* Product rows */}
                        {products.filter(p => (p.category || 'Άλλα') === cat).map(p => {
                          const isDragging = draggingProductId === p.id;
                          return (
                            <div
                              key={p.id}
                              ref={el => { if (el) rowRefs.current[p.id] = el; }}
                              onDragOver={(e) => { if (dragType.current === 'product') onProductDragOver(e, p, cat); }}
                              onDrop={(e) => { if (dragType.current === 'product') onProductDrop(e); }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: '10px 0',
                                borderBottom: '1px solid var(--gray-100)',
                                background: highlightId === p.id ? '#fef3c7' : (isDragging ? '#dcfce7' : undefined),
                                opacity: isDragging ? 0.5 : 1,
                                transition: 'background 0.4s ease, opacity 0.15s ease',
                                borderRadius: highlightId === p.id ? 6 : undefined,
                              }}
                            >
                              <span
                                draggable
                                onDragStart={(e) => onProductDragStart(e, p, cat)}
                                onDragEnd={onProductDragEnd}
                                style={{ ...dragHandleStyle, color: isDragging ? '#22c55e' : 'var(--gray-300)' }}
                                title="Σύρετε για αλλαγή σειράς"
                              >⠿</span>
                              <div style={{ width: 220, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--gray-800)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                                <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>€{parseFloat(p.price).toFixed(2)} / {packagingLabel(p.packaging)}</div>
                              </div>
                              <input
                                type="number"
                                className="qty-input"
                                min="0"
                                value={quantities[p.id] || ''}
                                placeholder="0"
                                onChange={e => handleQty(p.id, e.target.value)}
                                onDragStart={(e) => e.preventDefault()}
                                style={{ flexShrink: 0 }}
                              />
                              {quantities[p.id] > 0 && (
                                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--blue-dark)', flexShrink: 0 }}>
                                  €{(parseFloat(p.price) * quantities[p.id]).toFixed(2)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {showBelow && <div style={dropLineStyle} />}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
